import { EmailFilledIcon } from '@blueprinthq/joy'
import {
  Box,
  Button,
  Flex,
  HStack,
  ModalBody,
  ModalContent,
  Text,
  VStack
} from '@chakra-ui/react'
import { WarningTriangle } from '@components/icons'
import moment from 'moment'
import React, { useState } from 'react'
import { useCalendarEvent } from '../../context/calendar-event.context'
import { NotificationType } from '../../models/event'
import { CalendarEventDetailBox } from '../calendar-event-detail-box'
import { EventModalFooter } from './event-modal-footer'
import { EventModalHeader } from './event-modal-header'

interface SendNotificationModalProps {
  onCancel: () => void
  onSubmit: (sendNotification?: boolean) => void
}

export const SendNotificationModal = ({
  onCancel,
  onSubmit
}: SendNotificationModalProps) => {
  const { user, currentEvent, notificationType } = useCalendarEvent()

  const [loadingAction, setLoadingAction] = useState<
    'manual' | 'notification' | null
  >(null)

  const eventData = currentEvent

  const handleSubmit = async (sendNotification: boolean) => {
    setLoadingAction(sendNotification ? 'notification' : 'manual')
    try {
      await onSubmit(sendNotification)
    } finally {
      setLoadingAction(null)
    }
  }

  if (!eventData) {
    return null
  }

  const sendClientReminders = true

  // Configuration based on notification type
  const getModalConfig = () => {
    switch (notificationType) {
      case NotificationType.UPDATE:
        return {
          title: 'Send appointment update notification',
          isCancelling: false,
          showMessagePreview: false,
          showWarning: !sendClientReminders
        }
      case NotificationType.CANCEL:
        return {
          title: 'Send cancellation notification',
          isCancelling: true,
          showMessagePreview: false,
          showWarning: !sendClientReminders
        }
      default:
        return {
          title: 'Send invite',
          isCancelling: false,
          showMessagePreview: true,
          showWarning: !sendClientReminders
        }
    }
  }

  const config = getModalConfig()

  // Warning state when reminders are off
  if (config.showWarning) {
    return (
      <ModalContent>
        <EventModalHeader showCloseButton={true} onClose={onCancel}>
          <Text>{config.title}</Text>
        </EventModalHeader>
        <ModalBody>
          <VStack gap="small">
            <Flex
              w="72px"
              h="72px"
              borderRadius="8px"
              justify="center"
              align="center"
              bg="#FFFBF0"
            >
              <WarningTriangle fill="#C24D15" width="24px" />
            </Flex>
            <Box>
              <Text as="span" fontWeight="bold">
                Reminders are turned off{' '}
              </Text>
              <Text as="span">
                {' '}
                for this client, so we're not able to send the appointment
                details automatically. To respect their privacy, please send
                them any notifications manually. The info is ready for you to
                copy below.
              </Text>
            </Box>
          </VStack>
          <CalendarEventDetailBox isCancelling={config.isCancelling} />
        </ModalBody>
        <EventModalFooter>
          <HStack spacing="xsmall" mt="small">
            <Button
              onClick={onCancel}
              isFullWidth
              borderRadius="8px"
              variant="outline"
            >
              Cancel
            </Button>
            <Button
              onClick={() => onSubmit(false)}
              isFullWidth
              borderRadius="8px"
            >
              I'll send manually
            </Button>
          </HStack>
        </EventModalFooter>
      </ModalContent>
    )
  }

  // Main modal content
  return (
    <ModalContent>
      <EventModalHeader showCloseButton={true} onClose={onCancel}>
        <Text>{config.title}</Text>
      </EventModalHeader>
      <ModalBody>
        <VStack paddingBottom="16px" alignItems="flex-start" gap="16px">
          {config.showMessagePreview && (
            <VStack spacing="xsmall" align="flex-start" w="100%">
              <HStack spacing="xsmall" align="flex-start">
                <EmailFilledIcon size="sm" />
                <Text fontSize="12px" fontWeight="bold">
                  Message preview
                </Text>
              </HStack>
              <Text color="gray">
                Hi {eventData?.patient?.firstName}{' '}
                {eventData?.patient?.lastName} - your {eventData.getDuration()}
                -minute {eventData.setting} session is confirmed for{' '}
                {moment(eventData.start).format('ddd MMM D')} at{' '}
                {moment(eventData.start).format('h:mm A')}. Questions? Email{' '}
                {user?.email}
              </Text>
            </VStack>
          )}
          <CalendarEventDetailBox isCancelling={config.isCancelling} />
          <HStack w="100%" gap="4px">
            <Button
              width="100%"
              borderRadius="8px"
              fontSize="16px"
              variant="outline"
              isLoading={loadingAction === 'manual'}
              onClick={() => handleSubmit(false)}
            >
              I'll send manually
            </Button>
            <Button
              type="submit"
              width="100%"
              borderRadius="8px"
              fontSize="16px"
              bg="primary"
              isLoading={loadingAction === 'notification'}
              onClick={() => handleSubmit(true)}
              isFullWidth
            >
              Send notification
            </Button>
          </HStack>
        </VStack>
      </ModalBody>
    </ModalContent>
  )
}
